import { createStore, createEffect, createEvent, combine } from 'effector';
import i18n from 'i18n';
import { $notificationStore } from '../notificationStore';
import { ApiUtils } from '@shared/utils';
import { AccountsApi } from '@shared/api/rest';
import { type IQueryParams, RequestStrategyEnum } from '@shared/api/models';
import type { AccountByIdResponse, AccountUpdateDetailsRequest, AccountUpdateRequest } from '@shared/api/rest/accounts';

const getByIdEffect = createEffect((params: IQueryParams<number>) => {
  return AccountsApi.getAccount(params.request, params.signal);
});

const updateDetailsEffect = createEffect((request: AccountUpdateRequest<AccountUpdateDetailsRequest>) => AccountsApi.updateDetails(request));

updateDetailsEffect.done.watch((result) => {
  const params: IQueryParams<number> = {
    request: result.result!.id,
  };
  getByIdEffect(params);
});

const clearEvent = createEvent();

const $dataStore = createStore<AccountByIdResponse | null>(null)
  .on(getByIdEffect.done, (_, data) => data.result ?? null)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getByIdEffect.pending, (_, data) => data)
  .on(getByIdEffect.fail, () => false)
  .on(getByIdEffect.done, () => false)
  .on(updateDetailsEffect.pending, (_, data) => data)
  .on(updateDetailsEffect.fail, () => false)
  .on(updateDetailsEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getByIdEffect, () => null)
  .on(getByIdEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getByIdEffect.done, () => null)
  .on(updateDetailsEffect, () => null)
  .on(updateDetailsEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(updateDetailsEffect.done, () => null)
  .reset(clearEvent);
$errorStore.watch((x) => ApiUtils.errorStoreActionForbiddenWatcher(x, $notificationStore.events.setError));

export const $accountByIdStore = {
  events: {
    get: ApiUtils.wrapperApi(getByIdEffect, RequestStrategyEnum.Debounce),
    updateDetails: updateDetailsEffect,
    clear: clearEvent,
  },
  store: combine({
    data: $dataStore,
    loading: $loadingStore,
    error: $errorStore,
  }),
};
